import { useCallback, useEffect, useRef, useState } from "react";

// The trigger and its body-portalled panel form one interaction area. Pointer
// focus must not race a touch click; dismissal stays suppressed until re-entry.
export function useKeyInteraction(triggerRef, panelRef) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const dismissed = useRef(false);
  const pointer = useRef(false);
  const clear = useCallback(() => clearTimeout(timer.current), []);
  const close = useCallback(() => {
    clear();
    dismissed.current = true;
    setOpen(false);
  }, [clear]);
  const enter = () => {
    clear();
    dismissed.current = false;
    timer.current = setTimeout(() => setOpen(true), 120);
  };
  const leave = () => {
    clear();
    timer.current = setTimeout(() => setOpen(false), 180);
  };
  const blur = e => {
    if (triggerRef.current?.contains(e.relatedTarget) || panelRef.current?.contains(e.relatedTarget)) return;
    dismissed.current = false;
    leave();
  };
  useEffect(() => clear, [clear]);
  useEffect(() => {
    if (!open) return undefined;
    const escape = e => {
      if (e.key !== "Escape") return;
      close();
      if (panelRef.current?.contains(document.activeElement)) triggerRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener("keydown", escape, true);
    return () => document.removeEventListener("keydown", escape, true);
  }, [open, close, panelRef, triggerRef]);
  return {
    open, close,
    triggerProps: {
      onPointerEnter: e => { if (e.pointerType !== "touch") enter(); },
      onPointerLeave: e => { if (e.pointerType !== "touch") leave(); },
      onPointerDown: () => { pointer.current = true; clear(); },
      onMouseDown: e => { pointer.current = true; clear(); e.stopPropagation(); },
      onFocus: () => { if (!pointer.current && !dismissed.current) enter(); },
      onBlur: e => { pointer.current = false; blur(e); },
      onClick: () => {
        clear();
        pointer.current = false;
        dismissed.current = open;
        setOpen(value => !value);
      },
    },
    panelProps: {
      onPointerEnter: clear,
      onPointerLeave: leave,
      onFocus: clear,
      onBlur: blur,
    },
  };
}
